// 貨幣 decimal_places 對照 — **必須與 backend `app/models/concerns/currency_support.rb`
// `CurrencySupport::CURRENCIES` 同步**。FE 用同步常數而非 API call 是因為 split modal 等
// 互動需要立即拿到 precision 算金額預覽 / cents 比對；任何 round-trip 會卡 UX。
//
// 未來新增貨幣 / 改 decimal_places：兩邊一起改。沒對齊會造成 split 金額預覽 vs perform!
// 出現 SPLIT_AMOUNTS_NOT_CONSERVED false-reject。
const CURRENCY_DECIMAL_PLACES = Object.freeze({
  TWD: 0,
  JPY: 0,
  KRW: 0,
  USD: 2,
  EUR: 2,
  SGD: 2,
  CNY: 2,
  HKD: 2,
});

export const DEFAULT_DECIMAL_PLACES = 2;

// 拿貨幣對應的小數位數；未知 / null currency 走 default 2。
// 大小寫保險：backend `CurrencySupport::CURRENCIES` 用大寫，但 API response 若中間經過
// transformKeys / toLowerCase（過去 PR 有案例）會變小寫；統一 toUpperCase 後 lookup，
// 避免 BE 推 0 位、FE 走預設 2 位導致 cents 比對 false-reject。
//
// 未知 (non-falsy) currency 走 console.warn 對齊 backend `resolve_precision` — BE/FE 兩邊
// 都該 fail-loud。falsy (null/undefined/'') 不 warn，那是 init 階段 currency 還沒讀進來的常態。
export function currencyDecimalPlaces(currency) {
  if (!currency) return DEFAULT_DECIMAL_PLACES;
  const dp = CURRENCY_DECIMAL_PLACES[String(currency).toUpperCase()];
  if (dp === undefined) {
    // eslint-disable-next-line no-console
    console.warn(
      '[currencyDecimalPlaces] Unknown currency=', currency,
      '— falling back to', DEFAULT_DECIMAL_PLACES,
      'decimal places. Check CurrencySupport::CURRENCIES list or caller typo.',
    );
    return DEFAULT_DECIMAL_PLACES;
  }
  return dp;
}

// 把金額按貨幣 precision 轉為「最小單位整數」（cent / 整數元 / 整數韓元）。
// 與 backend `(total * w / total_weight).round(precision)` + DB numeric(12,2) 對齊，
// 用於 FE submit 前的 Σ 守恆預檢 — Float 累加（0.1 + 0.2）的雜訊用整數比對消掉。
export function toMinorUnits(amount, currency) {
  const dp = currencyDecimalPlaces(currency);
  return Math.round(Number(amount) * Math.pow(10, dp));
}

// 與 toMinorUnits 反向：minor unit → display 字串，保留 currency 應有的小數位。
export function fromMinorUnits(minor, currency) {
  const dp = currencyDecimalPlaces(currency);
  return (Number(minor) / Math.pow(10, dp)).toFixed(dp);
}

// Display 金額時固定 2 位小數，不依 currency dp。
// 為何不用 `currencyDecimalPlaces(currency)`：實務上部分電商（即使 TWD）會傳帶小數的金額
// （例：599.5 TWD），若硬照 ISO 4217 把 TWD 截到 0 位，display 會把 599.5 變 600 silently truncate。
// Backend BigDecimal `to_s("F")` 最少帶一位小數（`BigDecimal("599").to_s("F") == "599.0"`），
// 此 helper 把所有金額統一補齊到 2 位（599 → 599.00 / 599.5 → 599.50 / 599.99 → 599.99）。
// ⚠️ 注意：split modal 與 AmountSplitter 仍以 currency dp（TWD=0）做守恆比對 — 若 TWD 訂單實際帶
// 小數金額進 split，會在後端 round 階段 silently 丟精度。要支援 fractional TWD 必須改 CurrencySupport
// + currency.js 把 TWD dp 改 2，不是只動 display。
export const DISPLAY_DECIMAL_PLACES = 2;

export function formatAmount(amount, _currency) {
  if (amount === null || amount === undefined || amount === '') return '';
  const n = Number(amount);
  if (Number.isNaN(n)) return String(amount);
  return n.toFixed(DISPLAY_DECIMAL_PLACES);
}
